import { get } from "svelte/store";
import type { SharedAccountState } from "../interfaces/SharedAccount";
import type { SharedAccountMessageHistoryData } from "../interfaces/SharedAccountMessageHistory";
import { sharedAccountState } from "../stores/sharedAccountStore";
import { sharedAccountUserLabels, resetSharedAccountUserLabels } from "../stores/sharedAccountUsers";
import { user } from "../stores/userStore";
import { Roles, serverlessRoutes } from "./constants";
import { errorTracker } from "./errorTracker";
import { getWithJwt } from "./requests";

const sharedTrainerChatIds = new Set<number>([13, 87649]);

let hasLoadedSharedAccountUsers = false;
let sharedAccountUsersRequest: Promise<Record<string, string>> | null = null;

export const isSharedTrainerChatEnabled = (
  currentUser?: { id?: number; roleId?: number } | null,
  currentSharedAccountState?: SharedAccountState | null
): boolean => {
  const sharedState = currentSharedAccountState || get(sharedAccountState);

  return Boolean(
    currentUser?.roleId === Roles.Trainer &&
      currentUser.id &&
      sharedTrainerChatIds.has(currentUser.id) &&
      sharedState.isRequired === true &&
      sharedState.isUnlocked === true &&
      sharedState.sessionToken
  );
};

export const clearSharedTrainerChatState = (): void => {
  hasLoadedSharedAccountUsers = false;
  sharedAccountUsersRequest = null;
  resetSharedAccountUserLabels();
};

export const fetchSharedAccountUsers = async (): Promise<Record<string, string>> => {
  const currentUser = get(user);

  if (!isSharedTrainerChatEnabled(currentUser)) {
    return get(sharedAccountUserLabels);
  }

  if (hasLoadedSharedAccountUsers) {
    return get(sharedAccountUserLabels);
  }

  if (sharedAccountUsersRequest) {
    return sharedAccountUsersRequest;
  }

  sharedAccountUsersRequest = (async () => {
    try {
      const response = await getWithJwt(`${serverlessRoutes.SHARED_ACCOUNT}/users`);
      if (response?.error) {
        throw new Error(response.error);
      }

      const labels = response?.data?.userLabelsById || {};

      sharedAccountUserLabels.set(labels);

      return labels;
    } catch (error) {
      errorTracker.captureError(error as Error, "warning", {
        context: "shared_trainer_chat",
        action: "fetch_shared_account_users",
      });
      return get(sharedAccountUserLabels);
    } finally {
      hasLoadedSharedAccountUsers = true;
      sharedAccountUsersRequest = null;
    }
  })();

  return sharedAccountUsersRequest;
};

export const fetchMessageHistory = async (
  messageId: number
): Promise<SharedAccountMessageHistoryData> => {
  const response = await getWithJwt(`${serverlessRoutes.MESSAGE}/${messageId}/history`);

  if (response?.error) {
    throw new Error(response.error);
  }

  return response.data as SharedAccountMessageHistoryData;
};

export const getSharedAccountUserLabel = (
  labels: Record<string, string>,
  sharedAccountUserId?: number | null,
  fallbackLabel?: string | null
): string => {
  if (fallbackLabel) {
    return fallbackLabel;
  }

  if (!sharedAccountUserId) {
    return "";
  }

  return labels[String(sharedAccountUserId)] || "";
};
